import type { CSSProperties } from "react";

/** Class-name join. No dependency, no runtime cost worth naming. */
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** Clamp to a range. Used by every draggable surface in the app. */
export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/** `72.5` -> `1:12`. Segment maths is all seconds; humans read clocks. */
export function formatTime(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Inline `--bd-i` stagger index for the animation classes. */
export function stagger(index: number): CSSProperties {
  return { "--bd-i": index } as CSSProperties;
}

/**
 * Player-facing text for a thrown value.
 *
 * Convex wraps a ConvexError's message with the request id, the function name,
 * the handler's file and line, and "Called by client" — none of which a player
 * should ever see in a toast. The clean payload we actually threw is on
 * `.data`, so prefer that and never fall back to the wrapped `.message`.
 */
export function errorText(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "data" in err) {
    const data = (err as { data: unknown }).data;
    if (typeof data === "string" && data.trim().length > 0) return data.trim();
  }
  if (err instanceof Error && err.message) {
    // Anything carrying Convex's wrapper is server noise, not a message.
    if (!/\[CONVEX|Called by client|Uncaught ConvexError/i.test(err.message)) {
      return err.message;
    }
  }
  return fallback;
}
